import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// TablePagination is the shared paging primitive for every list/table page.
// One total + page + size source of truth, controlled — the page owns state
// (typically via useTablePaginationState below) and TablePagination renders.
//
// Layout: "Showing X–Y of Z" on the left, page-size selector + page-number
// row + prev/next on the right. Wraps cleanly on mobile.

export const DEFAULT_PAGE_SIZES = [25, 50, 100] as const;

interface Props {
  total: number;
  page: number; // 1-indexed
  size: number;
  pageSizes?: readonly number[];
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  // Controls visibility when there's nothing to page through. Default: hide
  // the bar when total <= smallest page size, since no UX value is added.
  alwaysShow?: boolean;
  className?: string;
}

export function TablePagination({
  total,
  page,
  size,
  pageSizes = DEFAULT_PAGE_SIZES,
  onPageChange,
  onSizeChange,
  alwaysShow = false,
  className,
}: Props) {
  const { t } = useTranslation('common');
  const totalPages = Math.max(1, Math.ceil(total / size));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const from = total === 0 ? 0 : (safePage - 1) * size + 1;
  const to = Math.min(safePage * size, total);

  // Hide the bar entirely when there's no value: empty list AND we'd just
  // show "Showing 0 of 0". Keep the bar visible on a non-empty single page
  // so users can change page size.
  if (!alwaysShow && total === 0) {
    return null;
  }

  const pages = computePageRange(safePage, totalPages);

  return (
    <nav
      aria-label={t('pagination.label')}
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p className="text-xs text-muted-foreground">
        {t('pagination.showing', { from, to, total })}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t('pagination.pageSize')}</span>
          <Select
            value={String(size)}
            onValueChange={(v) => onSizeChange(Number(v))}
          >
            <SelectTrigger
              className="w-20"
              aria-label={t('pagination.pageSize')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage <= 1}
            aria-label={t('pagination.previous')}
          >
            <CaretLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t('pagination.previous')}</span>
          </Button>

          {pages.map((entry, i) =>
            entry === 'ellipsis' ? (
              <span
                key={`gap-${i}`}
                aria-hidden="true"
                className="px-1 text-sm text-muted-foreground"
              >
                {ELLIPSIS}
              </span>
            ) : (
              <Button
                key={entry}
                type="button"
                variant={entry === safePage ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPageChange(entry)}
                aria-label={t('pagination.gotoPage', { page: entry })}
                aria-current={entry === safePage ? 'page' : undefined}
                className="min-w-9 px-2"
              >
                {entry}
              </Button>
            ),
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage >= totalPages}
            aria-label={t('pagination.next')}
          >
            <span className="hidden sm:inline">{t('pagination.next')}</span>
            <CaretRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </nav>
  );
}

const ELLIPSIS = '…';

// computePageRange produces the visible page-number strip for the bar:
// always shows first, last, current and current ± 1, with 'ellipsis' fillers
// where the row gets too long to spell out. Maximum 7 visible entries.
//
// Examples (current = c, total = T):
//   T=5,  c=3 → 1 2 3 4 5
//   T=10, c=1 → 1 2 3 … 10
//   T=10, c=5 → 1 … 4 5 6 … 10
//   T=10, c=10 → 1 … 8 9 10
function computePageRange(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: Array<number | 'ellipsis'> = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  if (left > 2) out.push('ellipsis');
  for (let p = left; p <= right; p++) out.push(p);
  if (right < total - 1) out.push('ellipsis');

  out.push(total);
  return out;
}

interface PaginationStateOpts {
  // Distinct query-param prefix when more than one paginated table lives on
  // the same route. Default: bare `page` and `size`.
  prefix?: string;
  defaultSize?: number;
}

// useTablePaginationState reads/writes pagination from the URL so a refresh,
// share, or back/forward keeps the user where they were. Returns 1-indexed
// page and size, plus convenience offset/limit for API calls.
export function useTablePaginationState(opts: PaginationStateOpts = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = opts.prefix ? `${opts.prefix}_page` : 'page';
  const sizeKey = opts.prefix ? `${opts.prefix}_size` : 'size';
  const defaultSize = opts.defaultSize ?? DEFAULT_PAGE_SIZES[0];

  const page = useMemo(() => {
    const raw = Number(searchParams.get(pageKey));
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
  }, [searchParams, pageKey]);

  const size = useMemo(() => {
    const raw = Number(searchParams.get(sizeKey));
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : defaultSize;
  }, [searchParams, sizeKey, defaultSize]);

  function update(next: { page?: number; size?: number }) {
    setSearchParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        if (next.size !== undefined) {
          // Reset to page 1 on size change so the user doesn't land past the
          // new last page.
          if (next.size === defaultSize) merged.delete(sizeKey);
          else merged.set(sizeKey, String(next.size));
          merged.delete(pageKey);
        }
        if (next.page !== undefined) {
          if (next.page <= 1) merged.delete(pageKey);
          else merged.set(pageKey, String(next.page));
        }
        return merged;
      },
      { replace: true },
    );
  }

  return {
    page,
    size,
    setPage: (p: number) => update({ page: p }),
    setSize: (s: number) => update({ size: s }),
    // Convenience for API callers: 0-based offset and capped limit.
    offset: (page - 1) * size,
    limit: size,
  };
}
